/**
 * Owner: Masa Bayu
 * File: direction-resolve.ts
 * Date:Time: 2026-09-19 16:02
 * Description: BCP 47 / script → direction resolution hints (GL-03).
 */
import { assertDirection } from './direction';
import { normalizeLanguageId } from './language-id';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxScriptIdentity } from './script-identity';
/** Primary-language hints when script is unknown (metadata only). */
const PRIMARY_DIRECTION_HINTS = Object.freeze({
    ar: 'rtl',
    fa: 'rtl',
    ur: 'rtl',
    he: 'rtl',
    yi: 'rtl',
    en: 'ltr',
    ms: 'ltr',
    id: 'ltr',
    zh: 'ltr',
    ja: 'ltr',
    ko: 'ltr',
    ru: 'ltr',
    th: 'ltr',
    lo: 'ltr',
    km: 'ltr',
    my: 'ltr',
    hi: 'ltr',
});
/**
 * Resolve direction for a languageId (+ optional explicit override).
 * Override always wins. Does not run a bidi algorithm.
 */
export function resolveDirectionHint(languageIdRaw, override) {
    const relation = resolveLanguageScriptRelation(languageIdRaw);
    const parsed = normalizeLanguageId(languageIdRaw);
    if (override !== undefined) {
        const dir = assertDirection(override);
        return Object.freeze({
            languageId: parsed.languageId,
            scriptId: relation.resolvedScriptId,
            hintedDirection: dir,
            source: 'override',
            override: dir,
        });
    }
    if (relation.resolvedScriptId) {
        const script = createQxScriptIdentity({ scriptId: relation.resolvedScriptId });
        if (script.defaultDirection !== 'auto') {
            return Object.freeze({
                languageId: parsed.languageId,
                scriptId: script.scriptId,
                hintedDirection: script.defaultDirection,
                source: 'script_default',
                override: null,
            });
        }
    }
    const primaryHint = PRIMARY_DIRECTION_HINTS[parsed.primary];
    if (primaryHint) {
        return Object.freeze({
            languageId: parsed.languageId,
            scriptId: relation.resolvedScriptId,
            hintedDirection: primaryHint,
            source: 'language_hint',
            override: null,
        });
    }
    return Object.freeze({
        languageId: parsed.languageId,
        scriptId: relation.resolvedScriptId,
        hintedDirection: 'auto',
        source: 'auto_fallback',
        override: null,
    });
}
//# sourceMappingURL=direction-resolve.js.map